import { EnhancedBundleLineItem } from '~/utils/use-cart-items';
import { Box, Flex, Text } from 'theme-ui';
import CartQuantitySelector from '~/components/Cart/QuantitySelector';
import ShippingNote from '~/components/Generic/ShippingNote';
import IndividualProduct from './IndividualProduct';
import useProducts from '~/hooks/graphql/queries/use-products';
import { LineItemPart } from '@chordcommerce/react-autonomy';

type Props = {
  item: EnhancedBundleLineItem;
  confirmed?: boolean;
};

export type BundleProduct = LineItemPart & {
  optionalShippingNote: string | null | undefined;
};

const BundleLineItem = ({ item, confirmed = false }: Props) => {
  const { displayAmount, quantity, id, parts } = item;
  const priceWithoutDecimal = displayAmount.split('.')[0];
  const products = useProducts();

  const bundleProducts: BundleProduct[] = (parts || []).map((part) => {
    const product = products.find((product) => product?.variants?.find((variant) => variant?.sku === part.variant.sku));
    const variant = product?.variants?.find((variant) => variant?.sku === part.variant.sku);
    return { ...part, optionalShippingNote: variant?.optionalShippingNote };
  });

  const bundleHasShippingNotes = bundleProducts?.some((product) => product.optionalShippingNote);

  return (
    <Box
      sx={{
        background: 'white',
        width: '100%',
        borderRadius: '1.6rem 0 1.6rem 0',
        overflow: 'hidden',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        '> .lineItemKit:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'cloud',
        },
      }}
    >
      <Flex
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          borderBottom: `1px solid`,
          borderColor: 'cloud',
          height: '6.4rem',
          px: '1.6rem',
          alignItems: 'center',
          alignContent: 'center',
        }}
      >
        <Text
          as="p"
          sx={{
            width: '20rem',
            fontWeight: 'medium',
            fontSize: '1.6rem',
            lineHeight: '100%',
            textTransform: 'uppercase',
            color: 'black',
          }}
          dangerouslySetInnerHTML={{ __html: item.variant.name }}
        ></Text>
        <Flex sx={{ gap: '1.6rem' }}>
          <Flex sx={{ flexDirection: 'column', justifyContent: 'start', gap: '8px' }}>
            <Text
              sx={{
                fontSize: '1rem',
                textTransform: 'uppercase',
                fontWeight: '400',
                lineHeight: '100%',
                color: 'UIGray2',
              }}
            >
              Quantity
            </Text>
            <Text
              sx={{
                color: 'black',
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                lineHeight: '1.2rem',
                height: '1.8rem',
                fontWeight: 'medium',
              }}
              as="p"
            >
              {confirmed ? (
                quantity
              ) : (
                <CartQuantitySelector
                  forwardSx={{ p: 0, m: 0, lineHeight: 0 }}
                  aria-label="quantity selector"
                  label="quantity"
                  quantity={quantity}
                  lineItemId={id}
                />
              )}
            </Text>
          </Flex>
          <Flex sx={{ flexDirection: 'column', justifyContent: 'start', gap: '8px' }}>
            <Text
              sx={{
                fontSize: '1rem',
                textTransform: 'uppercase',
                fontWeight: '400',
                lineHeight: '100%',
                color: 'UIGray2',
              }}
            >
              Price
            </Text>
            <Text
              sx={{
                fontSize: '1.2rem',
                color: 'black',
                textTransform: 'uppercase',
                lineHeight: '1.2rem',
                fontWeight: 'medium',
              }}
              as="p"
            >
              {priceWithoutDecimal}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {bundleProducts?.map((product) => (
        <div className="lineItemKit" key={product.id}>
          <IndividualProduct kit product={product} />
        </div>
      ))}
      {bundleHasShippingNotes && (
        <Flex
          sx={{
            minHeight: '6.4rem',
            p: '1.6rem',
            gap: '1rem',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'center',
          }}
        >
          {bundleProducts.map((product) =>
            product.optionalShippingNote ? (
              <ShippingNote key={product.id} note={`${product.variant.name}: ${product.optionalShippingNote}`} />
            ) : null
          )}
        </Flex>
      )}
    </Box>
  );
};

export default BundleLineItem;
